import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";
import { Progress } from "@/components/ui/progress";

type StepId = "q1" | "q2-emotions" | "q2-life" | "q2-relationship" | "q2-family" | "q2b-family" | "q3" | "q4" | "q5" | "result";

interface Question {
  id: StepId;
  question: string;
  options: { label: string; next: StepId }[];
}

const questions: Record<string, Question> = {
  q1: {
    id: "q1",
    question: "What brings you here today?",
    options: [
      { label: "I'm struggling with my emotions or mental health", next: "q2-emotions" },
      { label: "I'm going through a major life change or loss", next: "q2-life" },
      { label: "I want to improve my relationship", next: "q2-relationship" },
      { label: "I'm seeking help for my child or family", next: "q2-family" },
      { label: "I'm not sure — I just know something needs to change", next: "q2-emotions" },
    ],
  },
  "q2-emotions": {
    id: "q2-emotions",
    question: "What resonates most with you?",
    options: [
      { label: "Constant worry, racing thoughts, or feeling on edge", next: "q3" },
      { label: "Sadness, low energy, or loss of motivation", next: "q3" },
      { label: "Lingering effects of a painful or traumatic experience", next: "q3" },
      { label: "Feeling burned out or emotionally exhausted", next: "q3" },
    ],
  },
  "q2-life": {
    id: "q2-life",
    question: "What kind of transition are you navigating?",
    options: [
      { label: "Career change or job loss", next: "q3" },
      { label: "Divorce, separation, or breakup", next: "q3" },
      { label: "Loss of a loved one", next: "q3" },
      { label: "A new chapter (moving, parenthood, retirement)", next: "q3" },
    ],
  },
  "q2-relationship": {
    id: "q2-relationship",
    question: "What's the biggest challenge?",
    options: [
      { label: "Communication breakdowns or frequent conflict", next: "q3" },
      { label: "Feeling disconnected or emotionally distant", next: "q3" },
      { label: "Rebuilding trust after a betrayal", next: "q3" },
      { label: "Navigating a major decision together", next: "q3" },
    ],
  },
  "q2-family": {
    id: "q2-family",
    question: "What's going on with your child or family?",
    options: [
      { label: "My child is struggling with anxiety, mood, or behavior", next: "q2b-family" },
      { label: "Our family is going through a difficult transition", next: "q2b-family" },
      { label: "There's conflict or communication breakdown at home", next: "q2b-family" },
      { label: "I'm worried about my child but not sure what they need", next: "q2b-family" },
    ],
  },
  "q2b-family": {
    id: "q2b-family",
    question: "What kind of support are you looking for?",
    options: [
      { label: "Therapy for my child individually", next: "q3" },
      { label: "Family therapy — we want to work on this together", next: "q3" },
      { label: "I'm not sure yet — I'd like guidance", next: "q3" },
    ],
  },
  q3: {
    id: "q3",
    question: "Have you been to therapy before?",
    options: [
      { label: "Yes, and I found it helpful", next: "q4" },
      { label: "Yes, but it wasn't a great experience", next: "q4" },
      { label: "No, this would be my first time", next: "q4" },
      { label: "I've thought about it but haven't taken the step", next: "q4" },
    ],
  },
  q4: {
    id: "q4",
    question: "What matters most to you in a therapist?",
    options: [
      { label: "Someone warm and empathetic", next: "q5" },
      { label: "Someone direct and solutions-focused", next: "q5" },
      { label: "Someone who gives me space to process at my own pace", next: "q5" },
      { label: "I'm not sure yet — I want to find out", next: "q5" },
    ],
  },
  q5: {
    id: "q5",
    question: "How would you like to get started?",
    options: [
      { label: "I'd like to schedule a free consultation", next: "result" },
      { label: "I want to learn more first", next: "result" },
      { label: "I'm ready to book a session", next: "result" },
    ],
  },
};

const stepOrder: StepId[] = ["q1", "q2-emotions", "q2-life", "q2-relationship", "q2-family", "q2b-family", "q3", "q4", "q5", "result"];

function getStepNumber(id: StepId): number {
  if (id === "result") return 7;
  if (id === "q1") return 1;
  if (id.startsWith("q2b")) return 3;
  if (id.startsWith("q2")) return 2;
  if (id === "q3") return 4;
  if (id === "q4") return 5;
  if (id === "q5") return 6;
  return 1;
}

function getResultMessage(answers: Record<string, string>): { heading: string; body: string } {
  const q1 = answers.q1 || "";
  const q2Key = Object.keys(answers).find((k) => k.startsWith("q2"));
  const q2 = q2Key ? answers[q2Key] : "";

  if (q1.includes("child or family")) {
    const q2b = answers["q2b-family"] || "";
    if (q2b.includes("child individually")) {
      return {
        heading: "Your child deserves the right support.",
        body: "Stillwater Therapy provides a safe, age-appropriate space for children to process their emotions, build coping skills, and thrive — with you involved every step of the way.",
      };
    }
    if (q2b.includes("together")) {
      return {
        heading: "Family therapy can help you reconnect.",
        body: "When the whole family is struggling, healing together makes a difference. Stillwater Therapy helps families improve communication, resolve conflict, and strengthen their bond.",
      };
    }
    return {
      heading: "We'll help you figure out the next step.",
      body: "Whether your child needs individual support or your family would benefit from working together, a free consultation is the best way to find the right path forward.",
    };
  }

  if (q1.includes("relationship")) {
    return {
      heading: "Stillwater Therapy is a great fit for you.",
      body: "Relationships take work, and asking for help is a sign of strength. Stillwater Therapy helps couples rebuild connection, improve communication, and move forward together.",
    };
  }

  if (q1.includes("life change")) {
    if (q2.includes("Loss of a loved one")) {
      return {
        heading: "Stillwater Therapy is here for you.",
        body: "Grief doesn't follow a timeline. Stillwater Therapy offers a compassionate space to honor your loss and find your way through it, at your own pace.",
      };
    }
    return {
      heading: "Stillwater Therapy can help you navigate this.",
      body: "Change — even positive change — can feel destabilizing. Stillwater Therapy helps you find your footing, process what you're leaving behind, and move forward with clarity.",
    };
  }

  if (q1.includes("not sure")) {
    return {
      heading: "That's completely okay.",
      body: "Not knowing exactly what you need is perfectly okay. A free consultation is a no-pressure way to explore whether Stillwater Therapy is the right fit for you.",
    };
  }

  // Emotions path
  if (q2.includes("worry") || q2.includes("racing")) {
    return {
      heading: "Stillwater Therapy specializes in this.",
      body: "Stillwater Therapy specializes in helping people move from constant overwhelm to grounded calm. Through evidence-based approaches, we'll work together to quiet the noise and build lasting resilience.",
    };
  }
  if (q2.includes("Sadness") || q2.includes("low energy")) {
    return {
      heading: "You don't have to do this alone.",
      body: "You don't have to push through this alone. Stillwater Therapy provides a supportive space to reconnect with your sense of purpose and rediscover what brings you meaning.",
    };
  }
  if (q2.includes("traumatic")) {
    return {
      heading: "Healing is possible.",
      body: "Healing from trauma takes courage — and the right support. Stillwater Therapy offers a safe, paced approach to help you process painful experiences without feeling overwhelmed.",
    };
  }
  if (q2.includes("burned out")) {
    return {
      heading: "You deserve more than survival mode.",
      body: "Burnout isn't a badge of honor — it's a signal. We'll work together to restore your energy, set boundaries, and reconnect with what matters to you.",
    };
  }

  return {
    heading: "Stillwater Therapy is a great fit for you.",
    body: "A free consultation is a no-pressure way to explore whether Stillwater Therapy is the right fit for you.",
  };
}

function getCtaText(answers: Record<string, string>): { text: string; href: string } {
  const q5 = answers.q5 || "";
  if (q5.includes("learn more")) {
    return { text: "Learn More About Our Approach", href: "#about" };
  }
  return { text: "Schedule a Free Consultation", href: "#contact" };
}

const TOTAL_STEPS = 6;

const QuestionnaireSection = () => {
  const [currentStep, setCurrentStep] = useState<StepId>("q1");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<StepId[]>([]);
  const [direction, setDirection] = useState(1);

  const handleSelect = useCallback(
    (optionLabel: string, nextStep: StepId) => {
      setAnswers((prev) => ({ ...prev, [currentStep]: optionLabel }));
      setHistory((prev) => [...prev, currentStep]);
      setDirection(1);
      setCurrentStep(nextStep);
    },
    [currentStep]
  );

  const handleBack = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setDirection(-1);
    setCurrentStep(prev);
  }, [history]);

  const handleReset = useCallback(() => {
    setAnswers({});
    setHistory([]);
    setDirection(-1);
    setCurrentStep("q1");
  }, []);

  const stepNum = getStepNumber(currentStep);
  const progress = currentStep === "result" ? 100 : ((stepNum - 1) / TOTAL_STEPS) * 100;
  const question = questions[currentStep];

  const variants = {
    enter: (d: number) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
  };

  return (
    <section id="questionnaire" className="questionnaire-section py-24 px-6 bg-secondary">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="questionnaire-heading text-3xl md:text-4xl font-heading font-medium text-foreground mb-4">
            Is Stillwater Right for You?
          </h2>
          <div className="w-16 h-px bg-accent mx-auto mb-4" />
          <p className="font-body text-muted-foreground font-light">
            Answer a few questions to find out how we can help.
          </p>
        </motion.div>

        {/* Progress */}
        <div className="questionnaire-progress mb-10">
          <div className="flex justify-between items-center mb-2">
            <span className="font-body text-xs text-muted-foreground uppercase tracking-wider">
              {currentStep === "result"
                ? "Complete"
                : `Question ${stepNum} of ${TOTAL_STEPS}`}
            </span>
            <span className="font-body text-xs text-muted-foreground">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} aria-label="Questionnaire progress" className="h-1.5 bg-border" indicatorClassName="bg-slate transition-transform duration-700 ease-in-out" />
        </div>

        {/* Question / Result */}
        <div className="questionnaire-content relative min-h-[320px]">
          <AnimatePresence mode="wait" custom={direction}>
            {currentStep === "result" ? (
              <motion.div
                key="result"
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className="questionnaire-result text-center"
              >
                {(() => {
                  const result = getResultMessage(answers);
                  const cta = getCtaText(answers);
                  return (
                    <>
                      <div className="questionnaire-result-icon w-14 h-14 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
                        <span className="text-2xl">✦</span>
                      </div>
                      <h3 className="questionnaire-result-heading text-2xl font-heading font-medium text-foreground mb-4">
                        {result.heading}
                      </h3>
                      <p className="font-body text-muted-foreground font-light leading-relaxed mb-8 max-w-lg mx-auto">
                        {result.body}
                      </p>
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a
                          href={cta.href}
                          className="questionnaire-result-cta inline-block bg-primary text-primary-foreground px-8 py-3 rounded-md font-body font-medium tracking-wider text-sm uppercase hover:opacity-90 transition-opacity"
                        >
                          {cta.text}
                        </a>
                        <button
                          onClick={handleReset}
                          className="questionnaire-reset flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Start Over
                        </button>
                      </div>
                    </>
                  );
                })()}
              </motion.div>
            ) : question ? (
              <motion.div
                key={currentStep}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: "easeInOut" }}
              >
                <h3 className="questionnaire-question text-xl md:text-2xl font-heading font-medium text-foreground text-center mb-8">
                  {question.question}
                </h3>
                <div className="questionnaire-options space-y-3">
                  {question.options.map((opt) => (
                    <button
                      key={opt.label}
                      onClick={() => handleSelect(opt.label, opt.next)}
                      className="questionnaire-option w-full text-left px-5 py-4 rounded-md border border-border bg-background font-body text-foreground font-light hover:border-accent hover:bg-accent/5 transition-colors group"
                    >
                      <span className="flex items-center justify-between">
                        <span>{opt.label}</span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </span>
                    </button>
                  ))}
                </div>

                {history.length > 0 && (
                  <button
                    onClick={handleBack}
                    className="questionnaire-back flex items-center gap-2 mt-6 font-body text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back
                  </button>
                )}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default QuestionnaireSection;
